<script lang="ts" setup>
const { t: $t } = useI18n()

import { joinURL, withQuery } from 'ufo'
import type { ApiQuickSaleGetAllByMachineryId, CreateQuickSale } from '~/types'

const props = defineProps<{ machineryId: string }>()

const { quickSale } = useQuery()
const { data, isLoading } = quickSale.allByMachineryId(props.machineryId, true)
const { data: lastUsedTemplate } = quickSale.lastUsedTemplate()

const quickSaleColumn = computed((): OneDataColumn<ApiQuickSaleGetAllByMachineryId>[] => [
  {
    title: $t('customerModule.quickSale.description.label'),
    key: 'description',
  },
  {
    title: $t('customerModule.quickSale.createdAt.label'),
    key: 'createdAt',
    render: ({ createdAt }) => useDateAsString(createdAt),
  },
  {
    title: $t('customerModule.quickSale.createdBy.label'),
    key: 'createdBy',
    render: ({ createdBy, createdByEmail }) => createdBy.name ?? createdByEmail,
  },
])

const popupPdf = ref<null | { id: string, description: string }>(null)

// Mutate data
const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const createQuickSale = useMutation({
  mutationFn: $trpc.quickSale.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.machinery.details.createQuickSale.notification.error') }),
  onSuccess: async (quickSale) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['machines'] }),
      queryClient.invalidateQueries({ queryKey: ['quickSales'] }),
    ])
    quickSalePayload.value = null

    const url = withQuery(
      joinURL('/api/pdf/quickSale/details/', quickSale.id),
      { language: quickSalePDFLanguage.value },
    )
    window.open(url, '_blank')

    notification.success({ title: $t('customerModule.machinery.details.createQuickSale.notification.success'), duration: 4500 })
  },
})

const quickSalePayload = ref<CreateQuickSale | null>(null)
const quickSalePDFLanguage = ref<string>('de')
function saveQuickSale(data: CreateQuickSale & { language: string }) {
  const { language, ...createData } = data
  quickSalePDFLanguage.value = language
  return createQuickSale.mutate(createData)
}
</script>

<template>
  <CustomerModuleDataCard :title="$t('customerModule.quickSale.title')" :is-loading="isLoading">
    <QuickSalePopupPdf
      v-if="popupPdf"
      :payload="popupPdf"
      @close="popupPdf = null"
    />

    <template #headerExtra>
      <n-button
        type="primary"
        size="large"
        :disabled="false"
        @click="quickSalePayload = {
          machineryId: props.machineryId,
          description: '',
          includeVAT: true,
          templateFile: lastUsedTemplate?.templateFilePath ? { path: lastUsedTemplate.templateFilePath } : undefined,
        }"
      >
        <template #icon>
          <Icon name="material-symbols:point-of-sale" />
        </template>
        {{ $t('customerModule.machinery.details.createQuickSale.title') }}
      </n-button>
    </template>

    <CustomerModuleList
      :data="data ?? []"
      :action-buttons="() => ['pdf']"
      :columns="quickSaleColumn"
      placeholder-size="small"
      @pdf="row => popupPdf = row"
    />
  </CustomerModuleDataCard>

  <ThePopup
    :show="Boolean(quickSalePayload)"
    :title="$t('customerModule.machinery.details.partnerSales.createQuickSale.title')"
    width="95%"
    @close="quickSalePayload = null"
  >
    <QuickSaleCreationForm v-if="quickSalePayload" :payload="quickSalePayload" @save="saveQuickSale" />
  </ThePopup>
</template>
